import { State } from 'src/app/shared/models/state';
import { Race } from 'src/app/shared/models/race';
import { Country } from 'src/app/shared/models/country';
import { Gender } from 'src/app/shared/models/gender';
import { IAddressModel } from './addressModel';
import { AddressValidationStatus } from 'src/app/shared/services/address.service';

export class DemographicInfo {
  public gender: string;
  public races: Race[] = [];
  public streetAddress1: string;
  public streetAddress2: string;
  public city: string;
  public state: State;
  public region: string;
  public postalCode: string;
  public country: Country;
  public isShared: boolean;
  public validationMessage: string;
  public validationStatus: AddressValidationStatus;

  constructor(demographicInfo: any = undefined) {
    if (!demographicInfo) {
      return;
    }
    this.gender = demographicInfo.gender;
    this.races = demographicInfo.races;
    this.streetAddress1 = demographicInfo.streetAddress1;
    this.streetAddress2 = demographicInfo.streetAddress2;
    this.city = demographicInfo.city;
    this.state = State.parse(demographicInfo.state);
    this.region = demographicInfo.region;
    this.postalCode = demographicInfo.postalCode;
    this.country = Country.parse(demographicInfo.country);
    this.isShared = !!demographicInfo.isShared;
    this.updateValidationStatus(
      demographicInfo.validationStatus,
      demographicInfo.validationMessage
    );
  }

  public updateValidationStatus = (
    validationStatus: AddressValidationStatus,
    validationMessage: string = null
  ): DemographicInfo => {
    if (!validationStatus) {
      this.validationStatus = null;
      this.validationMessage = null;
      return this;
    }
    this.validationStatus = validationStatus;
    if (validationMessage) {
      this.validationMessage = validationMessage;
      return this;
    }
    //validationMessage not set, but validationStatus is
    if (this.validationStatus === 'Success') {
      this.validationMessage = 'Verified Address';
    } else {
      this.validationMessage = 'Unverified address';
    }
    return this;
  };

  //a type safe way to setup a DemographicInfo from a IAddressModel
  public static Build(
    info: Omit<IAddressModel, 'stateId' | 'countryId'>,
    state: State,
    country: Country
  ) {
    return new DemographicInfo({
      ...info,
      state: state,
      country: country,
    });
  }

  //concatenates city state, and zip
  getAddressLine3(): string {
    if (!this.hasAddressLine3()) return '';

    const addressLine3 = `
      ${this.city ? `${this.city}${this.state || this.region ? ',' : ''}` : ''}
      ${this.state ? `${this.state.code}` : this.region ? ` ${this.region}` : ''}
      ${this.postalCode ? ` ${this.postalCode}` : ''}`
      .split(/\s+/)
      .join(' ')
      .trim();

    if (addressLine3.endsWith(',')) {
      return addressLine3.substring(0, addressLine3.length - 1);
    }

    return addressLine3;
  }

  private hasAddressLine3 = () => this.city || this.state || this.region || this.postalCode;

  /** check if we can use the address on billing page */
  isUsableForBillingAddress = (): boolean => {
    if (this.city && this.streetAddress1 && this.country?.code2 && this.postalCode) {
      if (this.region || this.state) {
        return true;
      }
    }
    return false;
  };

  hasLocation = (): boolean =>
    this.hasAddressLine3() || this.streetAddress1 || this.streetAddress2 || this.country
      ? true
      : false;

  get genderInfo(): string {
    if (this.gender && this.gender === Gender.NonBinary) {
      return 'Non-binary';
    } else if (this.gender && this.gender === Gender.PreferNotToAnswer) {
      return 'Prefer not to answer';
    }

    return this.gender;
  }
}
